export const FOOTER_CONTENT = {
  roadmap: {
    title: 'Precision Engineering Roadmap',
    content: (
      <div className="space-y-8">
        <div className="space-y-3">
          <p className="text-sm md:text-base text-slate-600 font-medium leading-relaxed">
            EvalNDT is built on a modular architecture designed for high-frequency signal analysis. 
            Our 2026 milestones prioritize automation without sacrificing Level III oversight.
          </p>
        </div>
        
<div className="grid grid-cols-1 md:grid-cols-2 gap-5">
  {[
    {
      label: 'Signal Reconstruction',
      status: 'Active',
      desc: 'High-fidelity parsing of S-Scan and A-Scan data sets with synchronized encoded position tracking.',
      color: 'bg-emerald-500'
    },
    {
      label: 'Sensitivity Calibration',
      status: 'Stable',
      desc: 'Amplitude normalization via TCG (Time Corrected Gain) to ensure uniform sensitivity across the full focal range.',
      color: 'bg-blue-500'
    },
    {
      label: 'Indication Profiling',
      status: 'In-Test',
      desc: 'S-Scan based defect characterization using peak amplitude and -6dB drop logic for precise sizing.',
      color: 'bg-indigo-500'
    },
    {
      label: 'Compliance Engine',
      status: 'Stable',
      desc: 'Generation of traceable PAUT reports including S-Scan snapshots, A-Scan gates, and TCG calibration records.',
      color: 'bg-emerald-500'
    }
  ].map((item, i) => (
    <div
      key={i}
      className="flex flex-col p-6 bg-slate-50 rounded-[2rem] border-2 border-slate-100 hover:border-indigo-400 transition-all"
    >
      <div className="flex items-center justify-between mb-2.5">
        <span className="text-[11px] md:text-[13px] font-black text-slate-900 uppercase tracking-tight">
          {item.label}
        </span>
        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
          <div className={`w-1.5 h-1.5 rounded-full ${item.color}`} />
          {item.status}
        </span>
      </div>
      <p className="text-[11px] md:text-[13px] text-slate-600 leading-relaxed font-medium">
        {item.desc}
      </p>
    </div>
  ))}
</div>


      </div>
    )
  },

  privacy: {
    title: 'Privacy & Information Security',
    content: (
      <div className="space-y-6">
        <div className="bg-slate-900 p-6 rounded-3xl text-white relative overflow-hidden">
          <div className="relative z-10">
            <h4 className="text-xs md:text-sm font-black uppercase tracking-widest text-indigo-400 mb-2">Security Architecture</h4>
            <p className="text-sm md:text-base font-medium opacity-90 leading-relaxed">
              We employ military-grade AES-256 encryption for data in transit and strictly enforce a non-persistent session policy.
            </p>
          </div>
          <div className="absolute -right-4 -bottom-4 opacity-10 text-6xl">🔒</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h5 className="text-[10px] md:text-[11px] font-black text-indigo-600 uppercase tracking-[0.2em]">User Protections</h5>
            <ul className="space-y-3">
              {[
                { t: 'Zero Profiling', d: 'No behavioral tracking or metadata harvesting.' },
                { t: 'No Third-Party Access', d: 'Your identity is never shared with OEMs or partners.' }
              ].map((item, i) => (
                <li key={i} className="space-y-1">
                  <p className="text-xs md:text-sm font-black text-slate-800">{item.t}</p>
                  <p className="text-[11px] md:text-xs text-slate-500 font-medium">{item.d}</p>
                </li>
              ))}
            </ul>
          </div>
          <div className="space-y-4">
            <h5 className="text-[10px] md:text-[11px] font-black text-indigo-600 uppercase tracking-[0.2em]">Audit Readiness</h5>
            <ul className="space-y-3">
              {[
                { t: 'Immutable Logs', d: 'Session logs for internal quality audits only.' },
                { t: 'GDPR / CCPA', d: 'Full compliance with global data right standards.' }
              ].map((item, i) => (
                <li key={i} className="space-y-1">
                  <p className="text-xs md:text-sm font-black text-slate-800">{item.t}</p>
                  <p className="text-[11px] md:text-xs text-slate-500 font-medium">{item.d}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    )
  },

  data: {
    title: 'Data Sovereignty & Compliance',
    content: (
      <div className="space-y-6">
        <div className="p-1 bg-indigo-50 rounded-2xl border border-indigo-100">
          <div className="bg-white p-5 rounded-[14px] shadow-sm">
            <p className="text-sm md:text-lg font-black text-slate-800 leading-snug">
              Inspection data is the <span className="text-indigo-600">User's Exclusive Intellectual Property.</span>
            </p>
            <p className="text-[11px] md:text-sm text-slate-500 mt-2 font-medium">
              EVALNDT acts solely as a processing layer. We do not claim ownership of indications, scans, or generated logic.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { h: 'No AI Training', p: 'User data is strictly excluded from machine learning sets.' },
            { h: 'Volatile Memory', p: 'Files reside in RAM during analysis and are purged on exit.' },
            { h: 'Encrypted Export', p: 'Reports are generated with SHA-256 checksums for integrity.' },
            { h: 'Zero Persistence', p: 'No local storage cookies or long-term data caching.' }
          ].map((item, i) => (
            <div key={i} className="p-4 border border-slate-100 rounded-2xl hover:bg-slate-50 transition-colors">
              <h5 className="text-[10px] md:text-xs font-black text-slate-800 uppercase mb-1">{item.h}</h5>
              <p className="text-[11px] md:text-xs text-slate-500 font-medium leading-tight">{item.p}</p>
            </div>
          ))}
        </div>
      </div>
    )
  },

  contact: {
    title: 'Technical Support Gateway',
    content: (
      <div className="space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
          <div className="space-y-4 text-center md:text-left">
            <h4 className="text-sm md:text-lg font-black text-slate-800 leading-tight">
              Direct Access to Specialized Engineering Support.
            </h4>
            <p className="text-xs md:text-sm text-slate-500 font-medium">
              Our support team consists of NDT software specialists and Level III consultants.
            </p>
          </div>
          
          <div className="p-6 bg-slate-900 rounded-[2.5rem] shadow-2xl border border-slate-800 relative group">
            <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-2">Central Node</p>
            <p className="text-lg md:text-2xl font-mono text-white break-all select-all group-hover:text-indigo-200 transition-colors">
              support@evalndt.com
            </p>
            <div className="mt-4 flex gap-2">
              <div className="px-3 py-1 bg-white/5 rounded-full text-[9px] font-black text-white/40 uppercase tracking-widest">Global</div>
              <div className="px-3 py-1 bg-white/5 rounded-full text-[9px] font-black text-white/40 uppercase tracking-widest">24hr Monitor</div>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-4 p-6 bg-amber-50/50 border border-amber-100 rounded-[2rem]">
          <div className="text-3xl md:text-4xl shrink-0 text-center">📋</div>
          <div>
            <p className="text-xs md:text-sm font-black text-amber-900 uppercase mb-1 tracking-tight">Expedited Ticket Requirements</p>
            <p className="text-[11px] md:text-[13px] text-amber-800/80 leading-relaxed font-medium">
              To bypass standard triage, please provide: <span className="font-bold underline">Acquisition Hardware Model</span>, 
              <span className="font-bold underline">Scan File Extension</span> (.uvdata, .rdt, etc.), and <span className="font-bold underline">Method Code</span>.
            </p>
          </div>
        </div>
      </div>
    )
  }
};